import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LoadingSpinner = () => (
  <div style={{
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    minHeight: '100vh', backgroundColor: '#f5f5f5'
  }}>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      <div style={{
        width: 50, height: 50,
        border: '4px solid rgba(139, 195, 74, 0.2)',
        borderTop: '4px solid #7c3aed',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <p style={{ color: '#666', fontSize: '0.95rem', fontWeight: 500 }}>Loading…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
);

// Authenticated user — must also be active. Inactive users are bounced
// to the standalone "Pending Approval" page.
export const UserProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (user?.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  if (user?.role === 'business_partner') return <Navigate to="/bp/dashboard" replace />;
  if (user?.role === 'pos_merchant') return <Navigate to="/pos/dashboard" replace />;

  const validRoles = ['distributor', 'retailer', 'partner'];
  if (!validRoles.includes(user?.role)) return <Navigate to="/login" replace />;

  if (user?.account_status !== 'active') return <Navigate to="/user/pending" replace />;

  return children;
};

// POS merchant — must be active pos_merchant
export const POSProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (user?.role !== 'pos_merchant') return <Navigate to="/login" replace />;
  if (user?.account_status !== 'active') return <Navigate to="/user/pending" replace />;
  return children;
};

// Authenticated user, regardless of activation — used only by the pending page.
export const AnyAuthRoute = ({ children }) => {
  const { loading, isAuthenticated } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return children;
};

export const AdminProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (user?.role !== 'admin') return <Navigate to="/user/pending" replace />;
  return children;
};

export const BPProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (user?.role !== 'business_partner') return <Navigate to="/login" replace />;
  return children;
};

export const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (isAuthenticated()) {
    if (user?.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
    if (user?.role === 'business_partner') return <Navigate to="/bp/dashboard" replace />;
    if (user?.role === 'pos_merchant') {
      return user?.account_status === 'active'
        ? <Navigate to="/pos/dashboard" replace />
        : <Navigate to="/user/kyc" replace />;
    }
    if (user?.account_status === 'active') return <Navigate to="/user/dashboard" replace />;
    return <Navigate to="/user/kyc" replace />;
  }
  return children;
};
